import { assertEvent, assign } from 'xstate';

import { storeWantedTime } from '../utils';
import { type OrderingMachineAction } from './ordering-machine.actions.types';
import { isWantedTimeToday } from './ordering-machine.utils';

// ─────────────────────────────────────────────────────────────────────────────

const onGetInitialWantedTimeDone: OrderingMachineAction = assign({
  wantedTime({ event }) {
    assertEvent(event, 'xstate.done.actor.getInitialWantedTime');
    return event.output;
  },
  isWantedTimeAsap({ context, event }) {
    assertEvent(event, 'xstate.done.actor.getInitialWantedTime');
    return context.cart.wantedTimes[0] === event.output;
  },
});

const updateWantedTimes: OrderingMachineAction = assign({
  cart({ context, event }) {
    assertEvent(event, 'xstate.done.actor.fetchWantedTimes');
    const wantedTimes = event.output.wantedTimes;

    return {
      ...context.cart,
      wantedTimes,
    };
  },
  wantedTime({ context, event }) {
    assertEvent(event, 'xstate.done.actor.fetchWantedTimes');
    const wantedTimes = event.output.wantedTimes;

    const firstAvailableTimeslot = wantedTimes[0];

    if (!firstAvailableTimeslot) {
      return context.wantedTime;
    }

    // Do not overwrite the selected timeslot with the first one.
    if (!context.isWantedTimeAsap) {
      return context.wantedTime;
    }

    // Do not automatically bump the order to a day in the future.
    if (!isWantedTimeToday(firstAvailableTimeslot)) {
      return context.wantedTime;
    }

    return firstAvailableTimeslot;
  },
});

const storeWantedTimeAction: OrderingMachineAction = ({ context, event }) => {
  assertEvent(event, 'CHANGE_TIME');
  if (!context?.cart?.orderId || !event?.params?.wantedTime?.time) return;
  void storeWantedTime(context.cart.orderId, event.params.wantedTime.time);
};

const changeTime: OrderingMachineAction = assign({
  wantedTime({ event }) {
    assertEvent(event, 'CHANGE_TIME');
    return event.params.wantedTime;
  },
  isWantedTimeAsap({ context, event }) {
    assertEvent(event, 'CHANGE_TIME');
    return context.cart.wantedTimes[0] === event.params.wantedTime;
  },
  hasShownFutureDateWarning({ context, event }) {
    assertEvent(event, 'CHANGE_TIME');
    return event.params.isFutureDate || context.hasShownFutureDateWarning;
  },
});

const handleTimeslotUnavailable: OrderingMachineAction = assign({
  cart({ context }) {
    return {
      ...context.cart,
      wantedTimes: context.cart.wantedTimes.filter(
        (wantedTime) => wantedTime !== context.wantedTime,
      ),
    };
  },
  previousWantedTime({ context }) {
    return context.wantedTime;
  },
  wantedTime({ context }) {
    const nextTimeslot = context.cart.wantedTimes.find(
      (wantedTime) => wantedTime !== context.wantedTime,
    );
    if (!isWantedTimeToday(nextTimeslot)) {
      return context.wantedTime;
    }
    return nextTimeslot;
  },
  isWantedTimeAsap: true,
});

// ─────────────────────────────────────────────────────────────────────────────

export const OrderingTimeActions = {
  onGetInitialWantedTimeDone,
  changeTime,
  storeWantedTime: storeWantedTimeAction,
  updateWantedTimes,
  handleTimeslotUnavailable,
};
